import { useState, useEffect } from 'react';
import {
  Search, Filter, Eye, EyeOff, Star, Pin, Trash2,
  Loader2, X, AlertTriangle, ExternalLink, Flag, CheckSquare, Square
} from 'lucide-react';
import { Button } from '@/components/common';

const API_URL = import.meta.env.VITE_API_URL || '';

const getToken = () => localStorage.getItem('auth_token');

interface Post {
  id: string;
  user_id: string;
  title: string;
  content: string;
  category: string;
  tags_json?: string;
  country_code?: string;
  views_count: number;
  likes_count: number;
  comments_count: number;
  is_pinned: number;
  is_featured: number;
  status: 'published' | 'hidden' | 'deleted';
  flagged_count: number;
  created_at: string;
  updated_at: string;
  author_name: string;
  author_email: string;
  author_avatar?: string;
}

function StatusBadge({ status, flagged }: { status: string; flagged?: number }) {
  const badges: Record<string, { className: string; label: string }> = {
    published: { className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', label: 'Published' },
    hidden: { className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', label: 'Hidden' },
    deleted: { className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', label: 'Deleted' },
  };

  const badge = badges[status] || badges.published;

  return (
    <div className="flex items-center gap-1">
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
        {badge.label}
      </span>
      {(flagged || 0) > 0 && (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 flex items-center gap-1">
          <Flag className="w-3 h-3" />
          {flagged}
        </span>
      )}
    </div>
  );
}

export default function Posts() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  // Modal states
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'hide' | 'publish' | 'delete' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Multi-select
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const limit = 20;

  // Clear selection when posts change
  useEffect(() => {
    setSelectedPosts(new Set());
  }, [posts]);

  const toggleSelectAll = () => {
    if (selectedPosts.size === posts.length) {
      setSelectedPosts(new Set());
    } else {
      setSelectedPosts(new Set(posts.map(p => p.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedPosts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedPosts(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedPosts.size === 0) return;
    if (!confirm(`Delete ${selectedPosts.size} selected post(s)? This cannot be undone.`)) return;

    setBulkDeleting(true);
    const token = getToken();

    try {
      await Promise.all(
        Array.from(selectedPosts).map(id =>
          fetch(`${API_URL}/api/admin/posts/${id}/status`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status: 'deleted' }),
          })
        )
      );
      setSelectedPosts(new Set());
      fetchPosts();
      setMessage({ type: 'success', text: `${selectedPosts.size} posts deleted` });
    } catch (error) {
      console.error('Error deleting posts:', error);
      setMessage({ type: 'error', text: 'Failed to delete some posts' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const fetchPosts = async () => {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (page * limit).toString(),
      });

      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (flaggedOnly) params.set('flagged', 'true');

      const response = await fetch(`${API_URL}/api/admin/posts?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page, search, statusFilter, flaggedOnly]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    fetchPosts();
  };

  const openActionModal = (post: Post, type: 'hide' | 'publish' | 'delete') => {
    setSelectedPost(post);
    setActionType(type);
    setActionReason('');
    setShowActionModal(true);
  };

  const handleStatusAction = async () => {
    if (!selectedPost || !actionType) return;
    setActionLoading(true);

    try {
      const status = actionType === 'hide' ? 'hidden' : actionType === 'publish' ? 'published' : 'deleted';

      const response = await fetch(`${API_URL}/api/admin/posts/${selectedPost.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, reason: actionReason }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Post ${actionType === 'delete' ? 'deleted' : actionType === 'hide' ? 'hidden' : 'published'} successfully` });
        setShowActionModal(false);
        fetchPosts();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Action failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Action failed' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleFeatureToggle = async (post: Post, field: 'is_featured' | 'is_pinned') => {
    try {
      const newValue = post[field] === 0;

      const response = await fetch(`${API_URL}/api/admin/posts/${post.id}/feature`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: newValue }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Post ${field === 'is_featured' ? 'featured' : 'pinned'} status updated` });
        fetchPosts();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Update failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Update failed' });
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Post Moderation
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Review and moderate community posts
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-xl flex items-center justify-between ${
          message.type === 'success'
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        }`}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by title or content..."
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="hidden">Hidden</option>
          </select>
          <label className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-slate-700 rounded-xl cursor-pointer">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => { setFlaggedOnly(e.target.checked); setPage(0); }}
              className="rounded"
            />
            <span className="text-gray-900 dark:text-white text-sm">Flagged only</span>
          </label>
          <Button type="submit" className="bg-coral-500 hover:bg-coral-600">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
        </form>
      </div>

      {/* Bulk Actions Bar */}
      {selectedPosts.size > 0 && (
        <div className="bg-coral-50 dark:bg-coral-900/20 border border-coral-200 dark:border-coral-800 rounded-xl p-3 mb-4 flex items-center justify-between">
          <span className="text-sm font-medium text-coral-700 dark:text-coral-300">
            {selectedPosts.size} post{selectedPosts.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedPosts(new Set())}
              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              Clear
            </button>
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Posts List */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-coral-500" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No posts found
          </div>
        ) : (
          <>
            {/* Select All Header */}
            <div className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50 border-b dark:border-slate-700 flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {selectedPosts.size === posts.length && posts.length > 0 ? (
                  <CheckSquare className="w-5 h-5 text-coral-500" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedPosts.size === posts.length ? 'Deselect All' : 'Select All'}
              </span>
            </div>

            <div className="divide-y divide-gray-200 dark:divide-slate-700">
              {posts.map((post) => (
                <div key={post.id} className={`p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 ${selectedPosts.has(post.id) ? 'bg-coral-50/50 dark:bg-coral-900/10' : ''}`}>
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSelect(post.id)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-1"
                    >
                      {selectedPosts.has(post.id) ? (
                        <CheckSquare className="w-5 h-5 text-coral-500" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                    {/* Author Avatar */}
                    {post.author_avatar ? (
                      <img
                        src={post.author_avatar}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-coral-100 dark:bg-coral-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-coral-600 dark:text-coral-400 font-medium">
                          {post.author_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white line-clamp-1">
                            {post.title}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                            {post.content}
                          </p>
                        </div>
                        <StatusBadge status={post.status} flagged={post.flagged_count} />
                      </div>

                      <div className="flex items-center gap-4 mt-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          By {post.author_name}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {post.category}
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {post.views_count} views
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {post.likes_count} likes
                        </span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      {/* Badges */}
                      <div className="flex items-center gap-2 mt-2">
                        {post.is_featured === 1 && (
                          <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Featured
                          </span>
                        )}
                        {post.is_pinned === 1 && (
                          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded text-xs flex items-center gap-1">
                            <Pin className="w-3 h-3" />
                            Pinned
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => { setSelectedPost(post); setShowPreviewModal(true); }}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                        title="Preview"
                      >
                        <ExternalLink className="w-4 h-4 text-gray-500" />
                      </button>
                      <button
                        onClick={() => handleFeatureToggle(post, 'is_featured')}
                        className={`p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors ${
                          post.is_featured === 1 ? 'text-yellow-500' : 'text-gray-400'
                        }`}
                        title={post.is_featured === 1 ? 'Unfeature' : 'Feature'}
                      >
                        <Star className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleFeatureToggle(post, 'is_pinned')}
                        className={`p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors ${
                          post.is_pinned === 1 ? 'text-blue-500' : 'text-gray-400'
                        }`}
                        title={post.is_pinned === 1 ? 'Unpin' : 'Pin'}
                      >
                        <Pin className="w-4 h-4" />
                      </button>
                      {post.status === 'published' ? (
                        <button
                          onClick={() => openActionModal(post, 'hide')}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                          title="Hide"
                        >
                          <EyeOff className="w-4 h-4 text-gray-500" />
                        </button>
                      ) : (
                        <button
                          onClick={() => openActionModal(post, 'publish')}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-slate-600 rounded-lg transition-colors"
                          title="Publish"
                        >
                          <Eye className="w-4 h-4 text-gray-500" />
                        </button>
                      )}
                      <button
                        onClick={() => openActionModal(post, 'delete')}
                        className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t dark:border-slate-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} posts
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Preview Modal */}
      {showPreviewModal && selectedPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Post Preview
              </h2>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              {/* Author */}
              <div className="flex items-center gap-3 mb-4">
                {selectedPost.author_avatar ? (
                  <img
                    src={selectedPost.author_avatar}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 bg-coral-100 dark:bg-coral-900/30 rounded-full flex items-center justify-center">
                    <span className="text-coral-600 dark:text-coral-400 font-medium">
                      {selectedPost.author_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {selectedPost.author_name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {selectedPost.author_email}
                  </p>
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {selectedPost.title}
              </h3>
              <div className="prose dark:prose-invert max-w-none">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {selectedPost.content}
                </p>
              </div>

              {/* Meta */}
              <div className="mt-6 pt-4 border-t dark:border-slate-700 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>Category: {selectedPost.category}</span>
                <span>{selectedPost.views_count} views</span>
                <span>{selectedPost.likes_count} likes</span>
                <span>{selectedPost.comments_count} comments</span>
                <span>Posted: {new Date(selectedPost.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full">
            <div className="p-6 border-b dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {actionType === 'delete' ? 'Delete Post' : actionType === 'hide' ? 'Hide Post' : 'Publish Post'}
              </h2>
              <button
                onClick={() => setShowActionModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                <h4 className="font-medium text-gray-900 dark:text-white line-clamp-1">
                  {selectedPost.title}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  By {selectedPost.author_name}
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-500 dark:text-gray-400 mb-2">
                  Reason {actionType === 'publish' ? '(optional)' : '(recommended)'}
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder={`Enter reason for ${actionType === 'delete' ? 'deleting' : actionType === 'hide' ? 'hiding' : 'publishing'} this post...`}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-slate-700 border-0 rounded-xl text-gray-900 dark:text-white resize-none"
                />
              </div>

              {actionType === 'delete' && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl text-red-700 dark:text-red-400 text-sm">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>This action cannot be undone. The post will be permanently deleted.</span>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowActionModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className={`flex-1 ${
                    actionType === 'delete'
                      ? 'bg-red-500 hover:bg-red-600'
                      : 'bg-coral-500 hover:bg-coral-600'
                  }`}
                  onClick={handleStatusAction}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : actionType === 'delete' ? (
                    <Trash2 className="w-4 h-4 mr-2" />
                  ) : actionType === 'hide' ? (
                    <EyeOff className="w-4 h-4 mr-2" />
                  ) : (
                    <Eye className="w-4 h-4 mr-2" />
                  )}
                  {actionType === 'delete' ? 'Delete' : actionType === 'hide' ? 'Hide' : 'Publish'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
